// frontend/src/components/tender/list/procurement-model.ts
//
// The one classifier behind the Tender Explorer. Every count, every filter and
// every cell reads a row built here, so a procurement can never be counted
// under one pill and listed under another (plan 13 section 3, h2r CaseQueue).
//
// Pure functions only: no React, no fetch. They are unit tested in
// procurement-model.test.ts. Nothing derived here is written back to a record.

import {
  GATE_SEQUENCE_SIX,
  RECORD_ACTS,
  TENDER_ACTS,
  actForStage,
  deriveTenderStatus,
  pendingGateSix,
  pendingStep,
  recordActForGate,
  recordActForStage,
  type Lifecycle,
  type TenderStatus,
} from '@/components/tender/workbench-config'
import { TENDER_STAGES } from '@/components/tender/types'
import {
  coworkerForStage,
  coworkerForStageFive,
  resolveCoworker,
  type Coworker,
} from '@/lib/coworkers'
import { STAGE_NAMES_FIVE } from '@/lib/naming'
import { STEP_WORDS, STEP_WORDS_FIVE } from '@/components/briefing/briefing-copy'
import type { PillTone } from '@/components/list-page/status-tokens'
import type { StatusIconKind } from '@/components/shared/status-icon'
import type { PendingApproval } from '@/types/contracts'
import type { GateDecision, PipelineContractAction, ProcurementItem } from './types'

// ---- vocabulary -------------------------------------------------------------

/**
 * A stage key on the Explorer. The six-stage keys are discover, define, source,
 * evaluate, negotiate and manage; `procure` and `award` belong to the
 * five-stage vocabulary and are kept so the older classifier still types.
 */
export type StageKey =
  | 'discover'
  | 'define'
  | 'source'
  | 'procure'
  | 'evaluate'
  | 'negotiate'
  | 'award'
  | 'manage'

export type { Lifecycle }

/** The six stages, in order, from the shared act list (RECORD_ACTS). */
export const STAGE_KEYS: ReadonlyArray<StageKey> = RECORD_ACTS.map((act) => act.key)

/** The five stages of the build before the six-stage lifecycle. Kept beside the six. */
export const STAGE_KEYS_FIVE: ReadonlyArray<StageKey> = [
  'define',
  'procure',
  'evaluate',
  'award',
  'manage',
]

/** The stage keys of a lifecycle. */
export function stageKeysFor(lifecycle: Lifecycle = 'six_stage'): ReadonlyArray<StageKey> {
  return lifecycle === 'five_stage' ? STAGE_KEYS_FIVE : STAGE_KEYS
}

/**
 * Stage names for every key the page can meet: the six from RECORD_ACTS (the
 * only stage source for the page) and the two five-stage names that differ.
 */
export const STAGE_NAME: Record<StageKey, string> = {
  ...(Object.fromEntries(RECORD_ACTS.map((act) => [act.key, act.title])) as Record<string, string>),
  procure: STAGE_NAMES_FIVE.procure,
  award: STAGE_NAMES_FIVE.award,
} as Record<StageKey, string>

/** The five stage names, from TENDER_ACTS. Kept for the five-stage build. */
export const STAGE_NAME_FIVE: Record<StageKey, string> = Object.fromEntries(
  TENDER_ACTS.map((act) => [act.id === 'market' ? 'procure' : act.id, act.title])
) as Record<StageKey, string>

export type RegimeKey = 'act' | 'psr'

export const REGIME_LABEL: Record<RegimeKey, string> = {
  act: 'Procurement Act',
  psr: 'PSR',
}

/** The policy rule set a regime opens in AI Policies. */
export const REGIME_RULE_SET: Record<RegimeKey, string> = {
  act: 'procurement_act_2023',
  psr: 'psr',
}

/** Short names for the pending-gate sub-line on the Waiting tile. Gate ids never print. */
export const GATE_SHORT_LABEL: Record<string, string> = {
  GD: 'Baseline',
  GR: 'Requirements',
  G1: 'Route approval',
  G2: 'Clarification approval',
  G3: 'Evaluation',
  G4: 'Position and award',
  G5: 'Contractual action',
}

/**
 * What the My Approvals Waiting tile calls each pending gate (plan 06 4.2):
 * "Baseline, route, scores and contract action". The Explorer tile keeps
 * GATE_SHORT_LABEL, which v2-s02 draws.
 */
export const APPROVALS_TILE_LABEL: Record<string, string> = {
  GD: 'Baseline',
  GR: 'Requirements',
  G1: 'Route',
  G2: 'Clarifications',
  G3: 'Scores',
  G4: 'Position and award',
  G5: 'Contractual action',
}

/** The hover keys of the five-stage gate wording, kept beside GATE_HOVER_KEY. */
export const GATE_HOVER_KEY_FIVE: Record<string, string> = {
  G1: 'explorer.gate.route_five',
  G2: 'explorer.gate.clarification',
  G3: 'explorer.gate.evaluation_five',
  G4: 'explorer.gate.award_five',
  G5: 'explorer.gate.review_five',
}

/** The five-stage short names. Kept unchanged beside the six-stage ones. */
export const GATE_SHORT_LABEL_FIVE: Record<string, string> = {
  G1: 'Route approval',
  G2: 'Clarification approval',
  G3: 'Evaluation lock',
  G4: 'Award approval',
  G5: 'Post-award review',
}

/** The key of the hover that explains a gate (lib/hover-copy/explorer.ts). */
export const GATE_HOVER_KEY: Record<string, string> = {
  GD: 'explorer.gate.baseline',
  GR: 'explorer.gate.requirements',
  G1: 'explorer.gate.route',
  G2: 'explorer.gate.clarification',
  G3: 'explorer.gate.evaluation',
  G4: 'explorer.gate.award',
  G5: 'explorer.gate.review',
}

export type StatusFilter =
  | 'all'
  | 'waiting'
  | 'in_progress'
  | 'awarded'
  | 'completed'
  | 'returned'

/** The six Live Queue statuses with today's classifier; "Returned" reads "Sent back". */
export const STATUS_FILTERS: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'waiting', label: 'Waiting on you' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'awarded', label: 'Awarded' },
  { id: 'completed', label: 'Completed' },
  { id: 'returned', label: 'Sent back' },
]

const STATUS_FILTER_OF: Record<TenderStatus, StatusFilter> = {
  needs_approval: 'waiting',
  in_progress: 'in_progress',
  draft: 'in_progress',
  awarded: 'awarded',
  completed: 'completed',
  returned: 'returned',
  cancelled: 'completed',
}

const FALLBACK_STATUS_WORD: Record<TenderStatus, string> = {
  needs_approval: 'Needs you',
  in_progress: 'In progress',
  draft: 'Draft',
  awarded: 'Awarded',
  completed: 'Complete',
  returned: 'Sent back',
  cancelled: 'Cancelled',
}

// ---- small formatters --------------------------------------------------------

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * "2 h", "3 days", "45 min". Null when there is no start time or it is in the
 * future. Hours round to the nearest hour, as v2-s02 draws them: a decision
 * asked for at 08:26 and read at 11:15 has waited "3 h".
 */
export function waitingLabel(
  requestedAt: string | null | undefined,
  now: number
): string | null {
  if (!requestedAt) return null
  const since = Date.parse(requestedAt)
  if (Number.isNaN(since)) return null
  const elapsed = now - since
  if (elapsed < MINUTE) return null
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min`
  if (elapsed < DAY) {
    const hours = Math.round(elapsed / HOUR)
    return hours >= 24 ? '1 day' : `${hours} h`
  }
  const days = Math.floor(elapsed / DAY)
  return days === 1 ? '1 day' : `${days} days`
}

function startOfDay(ms: number): number {
  const date = new Date(ms)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

/** "today", "yesterday", "3 days ago". Null when the date is missing. */
export function relativeDay(
  iso: string | null | undefined,
  now: number
): string | null {
  if (!iso) return null
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return null
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

/** A date in en-GB: "9 Oct 2026". */
export function formatDateGb(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** A long en-GB date: "30 June 2027". */
export function formatDateLongGb(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** A date with the time for hovers: "9 Oct, 11:00". */
export function formatDateTimeGb(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${day}, ${time}`
}

/** The currency symbol for a code ("GBP" gives "£"). */
export function currencySymbol(currency: string | null | undefined): string {
  const code = currency || 'GBP'
  try {
    const part = new Intl.NumberFormat('en-GB', { style: 'currency', currency: code })
      .formatToParts(0)
      .find((p) => p.type === 'currency')
    return part?.value ?? code
  } catch {
    return code
  }
}

/**
 * "£1.5M" as on s02. From £100,000 up the figure reads in millions with one
 * decimal (so £900,000 is "£0.9M"); below that it reads in full. A missing
 * value is null, never "£0".
 */
export function formatValueShort(
  value: number | null | undefined,
  currency: string | null | undefined
): string | null {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return null
  }
  const symbol = currencySymbol(currency)
  if (value >= 100_000) return `${symbol}${(value / 1_000_000).toFixed(1)}M`
  return `${symbol}${Math.round(value).toLocaleString('en-GB')}`
}

/**
 * The ten-step name under the stage name where the stage table's own label is
 * too short to read on its own (plan 06 5.1: stage 0 reads "Pipeline visibility").
 */
const WORKING_STEP_NAME: Readonly<Record<number, string>> = {
  0: 'Pipeline visibility',
  9: 'Renewal and exit',
}

/** "Route & Market Approval" becomes "Route and market approval". */
export function plainStep(label: string | null | undefined): string {
  const clean = (label ?? '').replace(/\s*&\s*/g, ' and ').replace(/\s+/g, ' ').trim()
  if (!clean) return ''
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase()
}

/** Removes the dashes and plus signs the server copy still carries. */
export function plainText(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/\s+[\u2014\u2013]\s+/g, ', ')
    .replace(/\s+\+\s+/g, ' and ')
    .replace(/[\u2014\u2013]/g, ', ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ---- the row ----------------------------------------------------------------

export interface ProcurementRow {
  ref: string
  fullTitle: string
  /** The short title, falling back to the full one. */
  title: string
  businessUnit: string | null
  authority: string | null
  value: number | null
  currency: string
  stageKey: StageKey
  stageName: string
  /** The ten-step label, for the Live Queue and the hover. */
  workingStep: string
  workingStepNumber: number
  coworker: Coworker | undefined
  status: TenderStatus
  statusFilter: StatusFilter
  statusWord: string
  statusTone: PillTone
  /** The filled icon beside the status word (plan 03 section 5.3). */
  statusKind: StatusIconKind
  /** The plain name of the waiting decision for the Waiting on you tile ("FM baseline"). */
  waitingName: string | null
  /** The action the row waits on, under the status word ("Confirm the scope Hannah found", plan 11 B.12). */
  rowLine: string | null
  /** Plan 11 B.12: the plain line under the title, from the server; null on rows without one. */
  summary: string | null
  /** "Stage 1 of 6" under the stage pill on the six-stage lifecycle; null otherwise. */
  stageLine: string | null
  /** A second line under the status pill: "Contract action". Null on every other row. */
  statusSub: string | null
  needsYou: boolean
  /**
   * Leo's contractual action on this row's live contract. A decision, but not a
   * gate: it is listed in My Approvals and is not counted as "Waiting on you"
   * here, so the tile, the pill and the amber rows always agree (plan 06 4.3).
   */
  contractAction: RowContractAction | null
  /** The row the Command Briefing leads with. */
  lead: boolean
  pendingGateId: string | null
  /** The waiting decision keeps the name it was recorded under, so its hover reads the older wording. */
  recordedStepName: boolean
  /** "Route approval". */
  pendingGateLabel: string | null
  /**
   * The decision as a sentence names it, the same words the Command Briefing
   * uses for it: "baseline and scope", "scores and preferred bidder".
   */
  pendingStepWords: string | null
  decides: string | null
  requestedAt: string | null
  waitingMs: number | null
  waiting: string | null
  regime: RegimeKey | null
  regimeRuleSet: string | null
  route: string | null
  routeShort: string | null
  routeCode: string | null
  /** "today", "3 days ago", or "live contract" for Manage. */
  refSub: string | null
  createdAt: string | null
  createdLabel: string | null
  owner: string | null
  /** The coworker's live line: "Grace scoring 4 responses". */
  liveLine: string | null
  /** "Started by Leo. Current contract ends 30 June 2027." */
  originLine: string | null
  expectedPublish: string | null
  submissionDeadline: string | null
  valueBand: string | null
  /** Lower-cased text the search matches. */
  haystack: string
}

export interface RowContractAction {
  title: string
  href: string
  requestedAt: string | null
  /** "Procurement", from Settings on the server. */
  decides: string | null
}

/** The words under the status pill of a row with a contractual action waiting. */
export const CONTRACT_ACTION_WORD = 'Contract action'

function contractActionOf(
  action: PipelineContractAction | null | undefined,
  ref: string
): RowContractAction | null {
  if (!action) return null
  return {
    title: action.title?.trim() || CONTRACT_ACTION_WORD,
    href: action.href?.trim() || `/tender/${encodeURIComponent(ref)}?stage=manage&focus=decision`,
    requestedAt: action.requested_at ?? null,
    decides: action.decides ?? null,
  }
}

export function regimeKeyOf(regime: string | null | undefined): RegimeKey | null {
  const value = (regime ?? '').toLowerCase()
  if (!value) return null
  if (value.includes('psr')) return 'psr'
  if (value.includes('procurement') || value === 'act') return 'act'
  return null
}

/** "2 h 49" under three hours, "4 h" under a day, then days (plan 03 section 5.1). */
export function explorerWaitingLabel(requestedAt: string | null | undefined, now: number): string | null {
  if (!requestedAt) return null
  const since = Date.parse(requestedAt)
  if (Number.isNaN(since)) return null
  const elapsed = now - since
  if (elapsed >= HOUR && elapsed < 3 * HOUR) {
    const hours = Math.floor(elapsed / HOUR)
    const minutes = Math.floor((elapsed - hours * HOUR) / MINUTE)
    return `${hours} h ${String(minutes).padStart(2, '0')}`
  }
  return waitingLabel(requestedAt, now)
}

/** The filled icon of a row's status: the server's kind, else what the row says. */
export function statusKindFor(
  serverKind: string | null | undefined,
  word: string,
  status: TenderStatus,
  needsYou: boolean
): StatusIconKind {
  const known: StatusIconKind[] = ['done', 'needs_you', 'working', 'waiting', 'gap', 'not_applicable', 'sent_back', 'locked']
  if (serverKind === 'completed') return 'done'
  if (serverKind && (known as string[]).includes(serverKind)) return serverKind as StatusIconKind
  if (needsYou) return 'needs_you'
  const clean = word.trim().toLowerCase()
  if (clean === 'sent back' || status === 'returned') return 'sent_back'
  if (clean === 'on track' || status === 'completed' || status === 'awarded') return 'done'
  if (status === 'cancelled') return 'not_applicable'
  return 'working'
}

function statusToneFor(word: string, status: TenderStatus, needsYou: boolean): PillTone {
  if (needsYou) return 'amber'
  const clean = word.trim().toLowerCase()
  if (clean === 'on track') return 'green'
  if (clean === 'sent back') return 'amber'
  if (status === 'completed' || status === 'awarded') return 'green'
  return 'blue'
}

function originLineOf(item: ProcurementItem): string | null {
  // The server writes this line, with the starter's name resolved through the roster.
  if (item.origin_line?.trim()) return item.origin_line.trim()
  const origin = item.origin
  if (!origin) return null
  const parts: string[] = []
  if (origin.flagged_by) parts.push(`Started by ${origin.flagged_by}.`)
  const end = formatDateLongGb(origin.contract_end)
  if (end) parts.push(`Current contract ends ${end}.`)
  return parts.length ? parts.join(' ') : null
}

/** The five-stage act of a backend stage number. Kept for the five-stage build. */
function stageKeyOfFive(stage: number): StageKey {
  const act = actForStage(stage)
  return act.id === 'market' ? 'procure' : act.id
}

const KNOWN_STAGE_KEYS: ReadonlySet<string> = new Set<string>([...STAGE_KEYS, ...STAGE_KEYS_FIVE])

/**
 * The stage a row reads on the six-stage lifecycle. The server's `act_key` wins
 * because only the server knows the legacy read rule (plan 02 section 4): beds
 * sits at backend stage 1 but reads Source, as its pending gate closes Source.
 * An older server sends no `act_key`; then the act of the pending gate is used
 * (rule 4 of the read rule), else the act of the backend stage.
 */
export function stageKeyForItem(item: Pick<ProcurementItem, 'act_key' | 'needs_you_gate' | 'gate_states' | 'current_stage'>): StageKey {
  const sent = item.act_key?.trim().toLowerCase()
  if (sent && KNOWN_STAGE_KEYS.has(sent)) return sent as StageKey
  const gate = item.needs_you_gate ?? pendingGateSix(item.gate_states)
  const act = recordActForGate(gate) ?? recordActForStage(item.current_stage ?? 0)
  return act.key
}

function waitingMsOf(iso: string | null | undefined, now: number): number | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null
  return now - Date.parse(iso)
}

/** "Stage 3 of 6" for a six-stage key (plan 11 B.12); null for any other key. */
export function stagePositionLine(stageKey: StageKey): string | null {
  const i = STAGE_KEYS.indexOf(stageKey)
  return i < 0 ? null : `Stage ${i + 1} of ${STAGE_KEYS.length}`
}

/**
 * The Procurement cell's second line (plan 11 B.12): the server's plain
 * summary, else the business unit and the value. The action line moves to the
 * Status cell.
 */
export function procurementSubline(row: Pick<ProcurementRow, 'summary' | 'businessUnit' | 'value' | 'currency'>): string {
  return row.summary ?? [row.businessUnit, formatValueShort(row.value, row.currency)].filter(Boolean).join(' · ')
}

/** The line under the status word (plan 11 B.12): the contract action word, else the row's action line. */
export function statusSubLine(row: Pick<ProcurementRow, 'statusSub' | 'rowLine'>): string | null {
  return row.statusSub || row.rowLine || null
}

/** One list row from one pipeline item. `decisions` come from approvals/mine. Six stages unless `lifecycle` says otherwise. */
export function buildRow(
  item: ProcurementItem,
  decisions: ReadonlyArray<GateDecision>,
  now: number,
  lifecycle: Lifecycle = 'six_stage'
): ProcurementRow {
  const six = lifecycle !== 'five_stage'
  const stage = item.current_stage ?? 0
  const stageKey = six ? stageKeyForItem(item) : stageKeyOfFive(stage)
  const status = deriveTenderStatus({
    current_stage: stage,
    gate_states: item.gate_states ?? {},
  })
  const pending = pendingStep({
    current_stage: stage,
    gate_states: item.gate_states ?? {},
  })
  // Six stages: the server names the pending gate (`needs_you_gate`); an older
  // server leaves it to the gate states, read in raising order so GD and GR count.
  const gateId = six
    ? item.needs_you_gate ?? pendingGateSix(item.gate_states)
    : pending?.gate ?? null
  const needsYou = six ? gateId !== null : pending !== null
  const gateLabels = six ? GATE_SHORT_LABEL : GATE_SHORT_LABEL_FIVE
  const decision = gateId
    ? decisions.find((d) => d.ref === item.tender_ref && d.gateId === gateId)
    : undefined
  const requestedAt =
    (gateId ? item.gate_states?.[gateId]?.requested_at : null) ??
    decision?.requestedAt ??
    null

  const contractAction = six ? contractActionOf(item.contract_action, item.tender_ref) : null
  const action = needsYou ? null : contractAction
  const showAction = action !== null
  const serverWord = item.status_word?.trim() || item.latest_action?.status_word?.trim() || null
  const completedRow = item.status_kind === 'completed' || item.instance_state === 'completed'
  const statusWord =
    needsYou || showAction ? 'Needs you' : serverWord ?? FALLBACK_STATUS_WORD[status]

  const regime = regimeKeyOf(item.regime)
  const step = TENDER_STAGES.find((s) => s.stage === stage)
  const workingStep = plainStep(WORKING_STEP_NAME[stage] ?? step?.label ?? `Stage ${stage}`)

  const refSub =
    stageKey === 'manage'
      ? 'live contract'
      : relativeDay(requestedAt ?? item.latest_action?.at ?? item.created_at, now)

  const coworker =
    (six ? resolveCoworker(item.coworker_key) ?? coworkerForStage(stageKey) : coworkerForStageFive(stageKey))
  const regimeText = regime ? REGIME_LABEL[regime] : ''

  const title = item.short_title?.trim() || item.title
  const owner = item.created_by ?? null

  return {
    ref: item.tender_ref,
    fullTitle: item.title,
    title,
    businessUnit: item.business_unit ?? null,
    authority: item.authority ?? null,
    value: item.estimated_value ?? null,
    currency: item.currency || 'GBP',
    stageKey,
    stageName: STAGE_NAME[stageKey],
    workingStep,
    workingStepNumber: stage + 1,
    coworker,
    status,
    statusFilter: needsYou ? 'waiting' : completedRow ? 'completed' : STATUS_FILTER_OF[status],
    statusWord,
    statusTone: statusToneFor(statusWord, status, needsYou || showAction),
    statusKind: statusKindFor(item.status_kind, statusWord, status, needsYou || showAction),
    waitingName: item.waiting_name?.trim() || null,
    rowLine: item.live_line?.trim() || null,
    summary: item.row_summary?.trim() || null,
    stageLine: six ? stagePositionLine(stageKey) : null,
    statusSub: showAction ? CONTRACT_ACTION_WORD : null,
    needsYou,
    contractAction,
    lead: six && item.lead === true,
    pendingGateId: gateId,
    recordedStepName: six && item.gate_name_recorded === true,
    pendingGateLabel: gateId ? gateLabels[gateId] ?? plainStep(pending?.label) : null,
    pendingStepWords: gateId ? (six ? STEP_WORDS : STEP_WORDS_FIVE)[gateId] ?? null : null,
    decides: decision?.decides ?? (action ? action.decides : null),
    requestedAt: action ? action.requestedAt : requestedAt,
    waitingMs: waitingMsOf(action ? action.requestedAt : requestedAt, now),
    waiting:
      needsYou || action ? (six ? explorerWaitingLabel : waitingLabel)(action ? action.requestedAt : requestedAt, now) : null,
    regime,
    regimeRuleSet: regime ? REGIME_RULE_SET[regime] : null,
    route: item.route_label ?? null,
    routeShort: item.route_short ?? null,
    routeCode: item.route_type ?? null,
    refSub,
    createdAt: item.created_at ?? null,
    createdLabel: formatDateGb(item.created_at),
    owner,
    liveLine: item.live_line?.trim() || item.latest_action?.summary?.trim() || null,
    originLine: originLineOf(item),
    expectedPublish: formatDateGb(item.expected_publish_date),
    submissionDeadline: formatDateGb(item.submission_deadline),
    valueBand: item.value_band ?? null,
    haystack: [
      item.tender_ref,
      item.title,
      item.short_title,
      item.authority,
      item.business_unit,
      coworker?.name,
      regimeText,
      item.route_label,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  }
}

/**
 * Rows in the order the Dashboard shows them (s02): the row the Command
 * Briefing leads with, then procurements that need a person, then the first
 * row that is simply in progress, then a row with a contractual action
 * waiting, then everything else, each group in the order the server sent it
 * (the seed order). s02 lists Hospital beds (2 h) above Agency nursing
 * (3 days), so the screen does not sort the amber rows by age, and it draws
 * the Preparing row (PSR-NHS-002) above Soft FM, where plan 06 5.1 put Soft FM
 * first. The screen wins.
 */
export function sortForDashboard(rows: ReadonlyArray<ProcurementRow>): ProcurementRow[] {
  const lead = rows.filter((row) => row.lead)
  const needs = rows.filter((row) => !row.lead && row.needsYou)
  const rest = rows.filter((row) => !row.lead && !row.needsYou)
  const actions = rest.filter((row) => row.contractAction)
  const others = rest.filter((row) => !row.contractAction)
  return [...lead, ...needs, ...others.slice(0, 1), ...actions, ...others.slice(1)]
}

// ---- filtering and counts ----------------------------------------------------

export interface ListFilters {
  /** A stage key, or '' for all. */
  stage: string
  status: string
  regime: string
  /** "Procurement" or "Commercial", or '' for anyone. */
  decides: string
  query: string
}

function matchesStage(row: ProcurementRow, stage: string): boolean {
  return !stage || stage === 'all' || row.stageKey === stage
}

function matchesStatus(row: ProcurementRow, status: string): boolean {
  return !status || status === 'all' || row.statusFilter === status
}

function matchesRegime(row: ProcurementRow, regime: string): boolean {
  const key = regimeKeyOf(regime)
  return !regime || regime === 'all' || (key !== null && row.regime === key)
}

function matchesDecides(row: ProcurementRow, decides: string): boolean {
  return !decides || decides === 'anyone' || row.decides?.toLowerCase() === decides.toLowerCase()
}

function matchesQuery(row: ProcurementRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  return !q || row.haystack.includes(q)
}

export function applyFilters(
  rows: ReadonlyArray<ProcurementRow>,
  filters: ListFilters
): ProcurementRow[] {
  return rows.filter(
    (row) =>
      matchesStage(row, filters.stage) &&
      matchesStatus(row, filters.status) &&
      matchesRegime(row, filters.regime) &&
      matchesDecides(row, filters.decides) &&
      matchesQuery(row, filters.query)
  )
}

export interface PillCounts {
  /** Dashboard group: one choice among all, waiting and the five stages. */
  all: number
  waiting: number
  stages: Record<StageKey, number>
  /** Regime group. Counts the rows that match the other filters. */
  regimes: { act: number; psr: number }
  /** Live Queue status select. Counts the rows that match the other filters. */
  statuses: Record<StatusFilter, number>
}

/**
 * Counts for the pills. The Dashboard stage group is one single-choice group,
 * so each of its counts ignores the stage and status choice and reads the
 * rows that match the search and the regime. The regime group and the status
 * select count the rows that match every other filter. In each case choosing
 * a pill lists exactly the number it shows.
 */
export function pillCounts(
  rows: ReadonlyArray<ProcurementRow>,
  filters: ListFilters,
  lifecycle: Lifecycle = 'six_stage'
): PillCounts {
  const stageGroup = applyFilters(rows, { ...filters, stage: '', status: '' })
  const forRegime = applyFilters(rows, { ...filters, regime: '' })
  const forStatus = applyFilters(rows, { ...filters, status: '' })

  const stages = Object.fromEntries(
    stageKeysFor(lifecycle).map((key) => [key, stageGroup.filter((row) => row.stageKey === key).length])
  ) as Record<StageKey, number>

  const statuses = Object.fromEntries(
    STATUS_FILTERS.map((s) => [s.id, forStatus.filter((row) => matchesStatus(row, s.id)).length])
  ) as Record<StatusFilter, number>

  return {
    all: stageGroup.length,
    waiting: stageGroup.filter((row) => row.statusFilter === 'waiting').length,
    stages,
    regimes: {
      act: forRegime.filter((row) => row.regime === 'act').length,
      psr: forRegime.filter((row) => row.regime === 'psr').length,
    },
    statuses,
  }
}

// ---- tiles ------------------------------------------------------------------

/**
 * The same line from gate ids alone ("G1", "G3"): the Explorer's names, in
 * process order. Pass GATE_SHORT_LABEL_FIVE for the five-stage names.
 */
export function gateSubLine(
  gateIds: ReadonlyArray<string | null | undefined>,
  labels: Record<string, string> = GATE_SHORT_LABEL
): string | null {
  const names = GATE_SEQUENCE_SIX.filter((gate) => gateIds.includes(gate) && labels[gate]).map(
    (gate) => labels[gate]
  )
  return names.length ? names.join(', ') : null
}

/** "Baseline, route approval, evaluation": a list of names read as one sentence, as s02 draws the tile. */
export function sentenceCase(text: string | null | undefined): string | null {
  const clean = (text ?? '').trim()
  return clean ? clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase() : null
}

/**
 * "FM baseline, beds route, nursing scores": the tile sub-line in plain words
 * (Settings `explorer_waiting_names_v3`), when the server names every waiting
 * row; else the gate labels below.
 */
export function waitingNamesLine(rows: ReadonlyArray<ProcurementRow>): string | null {
  const waiting = rows.filter((row) => row.needsYou)
  if (waiting.length === 0 || waiting.some((row) => !row.waitingName)) return null
  return waiting.map((row) => row.waitingName as string).join(', ')
}

/** "Route approval, Evaluation": the pending gates in process order, no repeats. */
export function waitingSubLine(rows: ReadonlyArray<ProcurementRow>): string | null {
  const named = waitingNamesLine(rows)
  if (named) return named
  const labels: string[] = []
  for (const gate of GATE_SEQUENCE_SIX) {
    const hit = rows.find((row) => row.pendingGateId === gate && row.pendingGateLabel)
    if (hit?.pendingGateLabel && !labels.includes(hit.pendingGateLabel)) {
      labels.push(hit.pendingGateLabel)
    }
  }
  return labels.length ? labels.join(', ') : null
}

/** "£x.xM in total" for the Live Queue footer, in the active currency. */
export function totalValueLabel(
  rows: ReadonlyArray<ProcurementRow>,
  currency: string
): string | null {
  const total = rows.reduce((sum, row) => sum + (row.value ?? 0), 0)
  return formatValueShort(total, currency)
}

// ---- Live Queue sorting ------------------------------------------------------

/** Sort position of a stage key. The five-stage names rank where their six-stage twins do. */
const STAGE_RANK: Record<StageKey, number> = {
  discover: 0,
  define: 1,
  source: 2,
  procure: 2,
  evaluate: 3,
  negotiate: 4,
  award: 4,
  manage: 5,
}

export type QueueSortKey = 'title' | 'stage' | 'value' | 'waiting' | 'created'

export function sortQueue(
  rows: ReadonlyArray<ProcurementRow>,
  key: QueueSortKey | null,
  direction: 'asc' | 'desc'
): ProcurementRow[] {
  if (!key) return [...rows]
  const factor = direction === 'asc' ? 1 : -1
  const stageOrder = (row: ProcurementRow) => STAGE_RANK[row.stageKey]
  const compare = (a: ProcurementRow, b: ProcurementRow): number => {
    switch (key) {
      case 'title':
        return a.title.localeCompare(b.title, 'en-GB')
      case 'stage':
        return stageOrder(a) - stageOrder(b)
      case 'value':
        return (a.value ?? -1) - (b.value ?? -1)
      case 'waiting':
        return (a.waitingMs ?? -1) - (b.waitingMs ?? -1)
      case 'created':
        return (Date.parse(a.createdAt ?? '') || 0) - (Date.parse(b.createdAt ?? '') || 0)
    }
  }
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => factor * compare(a.row, b.row) || a.index - b.index)
    .map(({ row }) => row)
}

// ---- approvals and value -----------------------------------------------------

/** approvals/mine items as the lookup the rows use for "who decides" and "since when". */
export function toGateDecisions(
  approvals: ReadonlyArray<PendingApproval> | null | undefined
): GateDecision[] {
  return (approvals ?? [])
    .filter((a) => a.gate_id)
    .map((a) => ({
      ref: a.tender_ref,
      gateId: a.gate_id as string,
      decides: a.owner_function ?? null,
      requestedAt: a.requested_at ?? null,
      raisedBy: a.raised_by ?? null,
    }))
}

function positiveNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/** What a time figure is measured against, when the value endpoint names no baseline (PA: "establish a baseline before claiming savings"). */
export const BASELINE_SHORT = 'against an agreed baseline'

/** The part of the value payload that names the baseline. */
type BaselineCarrier =
  | { baseline?: { status?: string | null; short?: string | null } | null }
  | null
  | undefined

/**
 * True when the value endpoint names a baseline but it is withdrawn, or it sends
 * no words for it: there is nothing to measure against, so no hours are claimed
 * (plan 13 W1: a page never writes "agreed" itself). A payload with no baseline
 * block at all (another build) is not withdrawn.
 */
export function baselineWithdrawn(value: BaselineCarrier): boolean {
  const baseline = value?.baseline
  if (!baseline) return false
  return baseline.status === 'withdrawn' || !(baseline.short ?? '').trim()
}

/** The baseline wording the value endpoint sends, else the default. Empty when the baseline is withdrawn. */
export function baselineShort(value: BaselineCarrier): string {
  if (baselineWithdrawn(value)) return ''
  return value?.baseline?.short?.trim() || BASELINE_SHORT
}

/** The source line under a time figure when no baseline block says otherwise. */
export const BASELINE_SOURCE = 'Source: agreed baselines and stage times'

/**
 * The source line under a time figure. An agreed baseline, or none named, keeps
 * the fixed line; any other status reads in the server's own words, so a
 * proposed baseline is never called agreed.
 */
export function baselineSource(value: BaselineCarrier): string {
  const baseline = value?.baseline
  const short = (baseline?.short ?? '').trim()
  if (!baseline || baseline.status === 'agreed' || !short) return BASELINE_SOURCE
  return `Source: stage times, ${short}`
}

/** "1,414 h". Null when the figure is missing or zero, so its tile hides. */
export function hoursLabel(hours: number | null | undefined): string | null {
  const value = positiveNumber(hours)
  return value === null ? null : `${Math.round(value).toLocaleString('en-GB')} h`
}

/** "21 days". Null when the figure is missing or zero. */
export function daysLabel(days: number | null | undefined): string | null {
  const value = positiveNumber(days)
  if (value === null) return null
  const rounded = Math.round(value)
  return rounded === 1 ? '1 day' : `${rounded} days`
}

/** A whole count, or null when missing or zero. */
export function countLabel(count: number | null | undefined): string | null {
  const value = positiveNumber(count)
  return value === null ? null : Math.round(value).toLocaleString('en-GB')
}

/** "63%" from a 0 to 1 share. Null when missing. */
export function shareLabel(share: number | null | undefined): string | null {
  const value = positiveNumber(share)
  if (value === null) return null
  return `${Math.round((value <= 1 ? value * 100 : value))}%`
}

// ---- five-stage helpers ----------------------------------------------------------
// The classifier as it read before the six-stage lifecycle. Same functions, the
// five-stage vocabulary: Define, Procure, Evaluate, Award, Manage.

/** `buildRow` on the five-stage lifecycle. */
export function buildRowFive(
  item: ProcurementItem,
  decisions: ReadonlyArray<GateDecision>,
  now: number
): ProcurementRow {
  return buildRow(item, decisions, now, 'five_stage')
}

/** `pillCounts` on the five-stage lifecycle. */
export function pillCountsFive(rows: ReadonlyArray<ProcurementRow>, filters: ListFilters): PillCounts {
  return pillCounts(rows, filters, 'five_stage')
}
